import type { Model } from "@/api/client";
import { useModelsStore } from "@/stores/models";
import { cn } from "@/lib/utils";

interface ModelSelectorProps {
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
}

export function ModelSelector({ selected, onChange }: ModelSelectorProps) {
  const { models, loading } = useModelsStore();

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i}>
            <div className="h-3 w-16 bg-slate-100 rounded mb-2 animate-subtle-pulse" />
            <div className="grid grid-cols-2 gap-2">
              <div className="h-10 bg-slate-100 rounded-md animate-subtle-pulse" />
              <div className="h-10 bg-slate-100 rounded-md animate-subtle-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Routed families are the headline choice; concrete models stay available for
  // pinning a specific version, grouped by provider underneath.
  const families = models.filter((m) => m.family);
  const groups = new Map<string, Model[]>();
  for (const model of models) {
    if (model.family) continue;
    const provider = model.id.split("/")[0] || "other";
    if (!groups.has(provider)) groups.set(provider, []);
    groups.get(provider)!.push(model);
  }

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const option = (model: Model) => {
    const isSelected = selected.has(model.id);
    return (
      <button
        key={model.id}
        type="button"
        onClick={() => toggle(model.id)}
        aria-pressed={isSelected}
        className={cn(
          "flex items-center gap-2.5 px-3 py-2.5 rounded-md border text-left text-[13px] font-medium transition-all duration-150 cursor-pointer",
          isSelected
            ? "border-brand-600 bg-brand-50 text-brand-950 ring-1 ring-brand-600/20"
            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
        )}
      >
        <div
          className={cn(
            "w-2 h-2 rounded-full shrink-0 transition-colors",
            isSelected ? "bg-brand-600" : "bg-slate-200"
          )}
        />
        <span className="min-w-0">
          <span className="block truncate">{model.name}</span>
          {model.resolved_name && (
            <span className="block truncate text-[11px] font-normal text-slate-400">
              &rarr; {model.resolved_name}
            </span>
          )}
        </span>
      </button>
    );
  };

  return (
    <div className="space-y-5">
      {families.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold tracking-[0.06em] uppercase text-slate-400 mb-2">
            AI assistants
          </p>
          <div className="grid grid-cols-2 gap-2">{families.map(option)}</div>
          <p className="mt-2 text-[12px] text-slate-500">
            Prism picks the concrete model behind each assistant, so it can be upgraded without
            touching your setup. Every answer records the model that produced it.
          </p>
        </div>
      )}

      {groups.size > 0 && (
        <div className="pt-4 border-t border-slate-200 space-y-5">
          <p className="text-[12px] font-semibold text-slate-500">Advanced: pin a specific model</p>
          {Array.from(groups).map(([provider, providerModels]) => (
            <div key={provider}>
              <p className="text-[11px] font-semibold tracking-[0.06em] uppercase text-slate-400 mb-2">
                {provider}
              </p>
              <div className="grid grid-cols-2 gap-2">{providerModels.map(option)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
